import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { relatoriosApi, type RelatorioClientes } from '@/features/relatorios/api/relatoriosApi';
import {
  ORDENACAO_PADRAO,
  alternarOrdenacao,
  ordenarClientes,
  type CampoOrdenacaoClientes,
  type OrdenacaoClientes,
} from '@/features/relatorios/components/clientesRelatorio';

const ATRASO_BUSCA_MS = 350;

/**
 * Estado do relatório de clientes: busca (com debounce, filtrada no
 * backend), opções da aba, ordenação e carregamento. Fica na página — e
 * não no componente — porque o CSV/impressão usam os mesmos dados e a
 * mesma ordenação. Só busca quando `habilitado`.
 */
export function useRelatorioClientes(opts: {
  inicio: string;
  fim: string;
  clienteId?: string;
  habilitado: boolean;
}) {
  const { inicio, fim, clienteId, habilitado } = opts;
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [incluirSemCompra, setIncluirSemCompra] = useState(false);
  const [somenteFechados, setSomenteFechados] = useState(false);
  const [ordenacao, setOrdenacao] = useState<OrdenacaoClientes>(ORDENACAO_PADRAO);
  const [dados, setDados] = useState<RelatorioClientes | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);
  const ultimaReq = useRef(0);

  useEffect(() => {
    const id = setTimeout(() => setBuscaAplicada(busca.trim()), ATRASO_BUSCA_MS);
    return () => clearTimeout(id);
  }, [busca]);

  useEffect(() => {
    if (!habilitado) return;
    const req = ++ultimaReq.current;
    setCarregando(true);
    setErro(null);
    relatoriosApi
      .clientes({
        inicio,
        fim,
        clienteId: clienteId || undefined,
        somenteFechados,
        incluirSemCompra,
        busca: buscaAplicada || undefined,
      })
      .then((r) => {
        if (req === ultimaReq.current) setDados(r);
      })
      .catch((e: unknown) => {
        if (req !== ultimaReq.current) return;
        setErro(e instanceof Error ? e.message : 'Erro ao carregar o relatório de clientes.');
      })
      .finally(() => {
        if (req === ultimaReq.current) setCarregando(false);
      });
  }, [habilitado, inicio, fim, clienteId, somenteFechados, incluirSemCompra, buscaAplicada, versao]);

  const recarregar = useCallback(() => setVersao((v) => v + 1), []);

  const ordenarPor = useCallback((campo: CampoOrdenacaoClientes) => {
    setOrdenacao((atual) => alternarOrdenacao(atual, campo));
  }, []);

  const clientesOrdenados = useMemo(
    () => (dados ? ordenarClientes(dados.clientes, ordenacao) : []),
    [dados, ordenacao],
  );

  return {
    dados,
    clientesOrdenados,
    carregando,
    erro,
    recarregar,
    busca,
    setBusca,
    /** Busca já enviada ao backend (após o debounce). */
    buscaAplicada,
    incluirSemCompra,
    setIncluirSemCompra,
    somenteFechados,
    setSomenteFechados,
    ordenacao,
    setOrdenacao,
    ordenarPor,
  };
}

export type EstadoRelatorioClientes = ReturnType<typeof useRelatorioClientes>;
